/**
 * The shape `graph_view.py` writes and the geometry the knowledge-graph page draws it in. Kept free of
 * JSX so the layout is tested on numbers: which band and row a concept lands on, where an edge runs.
 *
 * Bands stack top to bottom: outside prerequisites, then the stages in map order. Inside a band the
 * concepts flow like text, left to right and wrapping at the screen's width, in an order where each one
 * comes after the concepts of that band it requires and a chain runs on before the next one starts. A
 * chain with nothing before it in the band starts in the left-to-right order of what it requires above.
 */

export type GraphState = "known" | "learning" | "weak" | "ready" | "untested";
export type BandStatus = "done" | "active" | "todo";

export interface GraphBand {
	id: string;
	name: string;
	status: BandStatus | null;
	known: number;
	total: number;
}

/** A section already started that teaches the concept, with its page in the project's workspace. */
export interface GraphLesson {
	section: string;
	name: string;
	status: string;
	learned: string | null;
	page: string | null;
}

export interface GraphNode {
	slug: string;
	name: string;
	field: string;
	band: string;
	state: GraphState;
	body: string;
	last: string | null;
	lessons: GraphLesson[];
	requires: string[];
	requiredBy: string[];
	relates: string[];
}

export interface GraphView {
	topic: string;
	bands: GraphBand[];
	nodes: GraphNode[];
	edges: Array<{ from: string; to: string }>;
}

export interface PlacedNode {
	slug: string;
	x: number;
	y: number;
	w: number;
	h: number;
	label: string;
	state: GraphState;
}

export interface PlacedBand {
	id: string;
	y: number;
	h: number;
	label: string;
	status: string;
	count: string;
}

/**
 * Where an edge runs, by where the dependent sits: on a line below (`down`, bottom middle to top
 * middle), next on the same line (`side`, right middle to left middle), further along the same line
 * (`arc`, top middle to top middle, over what lies between), or higher up (`up`, top to bottom).
 */
export interface PlacedEdge {
	from: string;
	to: string;
	x1: number;
	y1: number;
	x2: number;
	y2: number;
	kind: "down" | "side" | "arc" | "up";
}

export interface GraphLayout {
	width: number;
	height: number;
	bands: PlacedBand[];
	nodes: PlacedNode[];
	edges: PlacedEdge[];
}

const STATES: readonly GraphState[] = ["known", "learning", "weak", "ready", "untested"];
const BAND_STATUS: Record<BandStatus, string> = { done: "已完成", active: "进行中", todo: "未开始" };

const PAD_X = 12;
const BAND_HEAD = 40;
const BAND_FOOT = 14;
const NODE_H = 24;
const LINE = 42;
const NODE_GAP = 12;
const NODE_PAD = 22;
const MIN_NODE_W = 40;
const ELLIPSIS = "…";

/** Roughly how wide a label draws at 13px: a CJK character is square, the rest about half. */
export function measureText(text: string): number {
	let w = 0;
	for (const ch of text) w += ch.charCodeAt(0) > 0x2e7f ? 13 : 7;
	return w;
}

function str(v: unknown): string {
	return typeof v === "string" ? v : "";
}

function strings(v: unknown): string[] {
	return Array.isArray(v) ? v.filter((s): s is string => typeof s === "string") : [];
}

function parseNode(raw: unknown): GraphNode | null {
	const n = raw as Record<string, unknown> | null;
	if (!n || typeof n !== "object" || typeof n.slug !== "string" || typeof n.name !== "string" || typeof n.band !== "string") return null;
	if (typeof n.state !== "string" || !(STATES as readonly string[]).includes(n.state)) return null;
	const lessons = (Array.isArray(n.lessons) ? n.lessons : []).map((l: Record<string, unknown>) => ({
		section: str(l?.section),
		name: str(l?.name),
		status: str(l?.status),
		learned: typeof l?.learned === "string" ? l.learned : null,
		page: typeof l?.page === "string" && l.page ? l.page : null,
	}));
	return {
		slug: n.slug,
		name: n.name,
		field: str(n.field),
		band: n.band,
		state: n.state as GraphState,
		body: str(n.body),
		last: typeof n.last === "string" ? n.last : null,
		lessons,
		requires: strings(n.requires),
		requiredBy: strings(n.requiredBy),
		relates: strings(n.relates),
	};
}

export function parseGraph(raw: unknown): { ok: true; graph: GraphView } | { ok: false; error: string } {
	const v = raw as Partial<Record<keyof GraphView, unknown>> | null;
	if (!v || typeof v !== "object" || !Array.isArray(v.bands) || !Array.isArray(v.nodes) || !Array.isArray(v.edges)) return { ok: false, error: "不是知识图谱：缺少 bands、nodes 或 edges" };
	const bands: GraphBand[] = [];
	for (const raw of v.bands as unknown[]) {
		const b = raw as Record<string, unknown> | null;
		if (!b || typeof b.id !== "string") return { ok: false, error: "图谱中有分带无法读取" };
		const status = b.status === "done" || b.status === "active" || b.status === "todo" ? b.status : null;
		bands.push({ id: b.id, name: str(b.name), status, known: Number(b.known) || 0, total: Number(b.total) || 0 });
	}
	const nodes: GraphNode[] = [];
	for (const raw of v.nodes as unknown[]) {
		const n = parseNode(raw);
		if (!n) return { ok: false, error: "图谱中有概念无法读取" };
		nodes.push(n);
	}
	const edges = (v.edges as Array<Record<string, unknown>>)
		.filter((e) => e && typeof e.from === "string" && typeof e.to === "string")
		.map((e) => ({ from: e.from as string, to: e.to as string }));
	return { ok: true, graph: { topic: str(v.topic), bands, nodes, edges } };
}

/** Everything a concept rests on and everything resting on it, the concept included. */
export function chainOf(view: GraphView, slug: string): Set<string> {
	const walk = (start: string, next: (s: string) => string[]) => {
		const seen = new Set([start]);
		const queue = [start];
		while (queue.length) for (const s of next(queue.shift()!)) if (!seen.has(s)) seen.add(s), queue.push(s);
		return seen;
	};
	const up = walk(slug, (s) => view.edges.filter((e) => e.to === s).map((e) => e.from));
	const down = walk(slug, (s) => view.edges.filter((e) => e.from === s).map((e) => e.to));
	return new Set([...up, ...down]);
}

function fit(text: string, max: number, measure: (t: string) => number): string {
	if (measure(text) <= max) return text;
	const chars = [...text];
	while (chars.length > 1 && measure(chars.join("") + ELLIPSIS) > max) chars.pop();
	return chars.join("") + ELLIPSIS;
}

/**
 * A band's concepts in reading order: each after the ones of the band it requires, a chain followed to
 * its end before the next starts, and a new chain picked by where what it requires sits above.
 */
function readingOrder(members: GraphNode[], placed: Map<string, PlacedNode>): GraphNode[] {
	const inBand = new Set(members.map((n) => n.slug));
	const done = new Set<string>();
	const ready = (n: GraphNode) => !done.has(n.slug) && n.requires.every((s) => !inBand.has(s) || done.has(s));
	const above = (n: GraphNode) => {
		const xs = n.requires.map((s) => placed.get(s)).filter((p): p is PlacedNode => Boolean(p)).map((p) => p.x + p.w / 2);
		return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : Infinity;
	};
	const order: GraphNode[] = [];
	while (order.length < members.length) {
		const starts = members.filter(ready);
		// A cycle the check let through leaves nothing ready; take what is left in view order.
		let current = starts.length ? starts.reduce((best, n) => (above(n) < above(best) ? n : best)) : members.find((n) => !done.has(n.slug))!;
		for (;;) {
			done.add(current.slug);
			order.push(current);
			const next = members.find((n) => n.requires.includes(current.slug) && ready(n));
			if (!next) break;
			current = next;
		}
	}
	return order;
}

export function layoutGraph(view: GraphView, width: number, measure: (t: string) => number = measureText): GraphLayout {
	const maxW = Math.max(MIN_NODE_W, width - 2 * PAD_X);
	const placed = new Map<string, PlacedNode>();
	const lineOf = new Map<string, number>();
	const bands: PlacedBand[] = [];
	let y = 0;
	let lineNo = 0;
	for (const band of view.bands) {
		const members = view.nodes.filter((n) => n.band === band.id);
		let lineY = y + BAND_HEAD;
		let x = PAD_X;
		let lines = members.length ? 1 : 0;
		for (const n of readingOrder(members, placed)) {
			const w = Math.min(maxW, measure(n.name) + NODE_PAD);
			if (x > PAD_X && x + w > PAD_X + maxW) {
				x = PAD_X;
				lineY += LINE;
				lines += 1;
				lineNo += 1;
			}
			placed.set(n.slug, { slug: n.slug, x, y: lineY, w, h: NODE_H, label: fit(n.name, w - NODE_PAD, measure), state: n.state });
			lineOf.set(n.slug, lineNo);
			x += w + NODE_GAP;
		}
		lineNo += 1;
		const h = BAND_HEAD + (lines ? (lines - 1) * LINE + NODE_H : 0) + BAND_FOOT;
		bands.push({ id: band.id, y, h, label: fit(band.name, width / 2, measure), status: band.status ? BAND_STATUS[band.status] : "", count: `已掌握 ${band.known}/${band.total}` });
		y += h;
	}
	const nodes = view.nodes.map((n) => placed.get(n.slug)).filter((p): p is PlacedNode => Boolean(p));
	const edges: PlacedEdge[] = [];
	for (const e of view.edges) {
		const a = placed.get(e.from);
		const b = placed.get(e.to);
		if (!a || !b) continue;
		const base = { from: e.from, to: e.to };
		if (lineOf.get(e.from) === lineOf.get(e.to)) {
			const between = nodes.some((n) => n.y === a.y && n.x > Math.min(a.x, b.x) && n.x < Math.max(a.x, b.x));
			edges.push(
				between || b.x < a.x
					? { ...base, kind: "arc", x1: a.x + a.w / 2, y1: a.y, x2: b.x + b.w / 2, y2: b.y }
					: { ...base, kind: "side", x1: a.x + a.w, y1: a.y + a.h / 2, x2: b.x, y2: b.y + b.h / 2 },
			);
		} else if (b.y > a.y) edges.push({ ...base, kind: "down", x1: a.x + a.w / 2, y1: a.y + a.h, x2: b.x + b.w / 2, y2: b.y });
		else edges.push({ ...base, kind: "up", x1: a.x + a.w / 2, y1: a.y, x2: b.x + b.w / 2, y2: b.y + b.h });
	}
	return { width, height: y, bands, nodes, edges };
}
